import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

interface PresenceContextType {
  onlineClientIds: Set<string>;
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

export const PresenceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [onlineClientIds, setOnlineClientIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let channel: RealtimeChannel | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout>;

    const setupPresence = async () => {
      if (!user) return;
      
      console.log("PresenceProvider: Setting up channel...");
      if (channel) {
          supabase.removeChannel(channel);
      }

      channel = supabase.channel('fairyrealm-presence', {
        config: {
          presence: {
            key: user.role === 'DOMINA' ? 'GODDESS' : user.id,
          },
        },
      });

      channel.on('presence', { event: 'sync' }, () => {
        if (!channel) return;
        const newState = channel.presenceState();
        const onlineIds = new Set<string>();
        
        for (const key in newState) {
          if (key === 'GODDESS') {
            onlineIds.add('GODDESS');
          }
          
          const presences = newState[key] as unknown as { client_id?: string, is_goddess?: boolean }[];
          if (presences && presences.length > 0) {
            const { client_id, is_goddess } = presences[0];
            if (client_id) {
              onlineIds.add(client_id);
            }
            if (is_goddess) {
              onlineIds.add('GODDESS');
            }
          }
        }
        setOnlineClientIds(onlineIds);
      });

      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log("PresenceProvider: Subscribed");
          await channel!.track({
            client_id: user.clientId,
            is_goddess: user.role === 'DOMINA'
          });
        }
        if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log("PresenceProvider: Channel status:", status, "retrying in 5s");
          clearTimeout(reconnectTimeout);
          reconnectTimeout = setTimeout(setupPresence, 5000);
        }
      });
    };

    setupPresence();

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && user) {
            console.log("PresenceProvider: Tab visible, re-syncing presence...");
            setupPresence();
        }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(reconnectTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user]);


  const value = useMemo(() => ({ onlineClientIds }), [onlineClientIds]);
  
  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = (): PresenceContextType => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};
